/**
 * What the app is allowed to tell you about.
 *
 * ON is the default and the absence of a saved setting IS on — the Worker and
 * worker/lib/notify.ts both read a missing row that way, so an unticked box here
 * is the ONLY thing that mutes anything. That is why this screen shows every
 * switch as on until someone deliberately turns one off.
 *
 * Only the notifications a person can actually receive are shown: money
 * notifications are raised for adults, so a child is never offered a switch that
 * could not have done anything.
 */
import type { NotificationType, Role } from '../../shared/types';
import { NOTIFY_GROUPS, notifyCatalogFor } from './meta';
import { CheckRow } from './ui';

export default function NotificationsField({
  role,
  value,
  onChange,
}: {
  role: Role;
  /** type -> enabled. A type that has never been saved arrives here as `true`. */
  value: Record<NotificationType, boolean>;
  onChange: (type: NotificationType, enabled: boolean) => void;
}) {
  const catalog = notifyCatalogFor(role);
  const groups = NOTIFY_GROUPS.filter((g) => catalog.some((n) => n.group === g));
  const mutedCount = catalog.filter((n) => !value[n.type]).length;

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-ink-soft">
        {mutedCount === 0
          ? 'Everything is on. Untick anything you’d rather not hear about.'
          : `${mutedCount} turned off. You won’t get a bell for ${
              mutedCount === 1 ? 'that one' : 'those'
            }.`}
      </p>

      {groups.map((group) => (
        <fieldset key={group} className="flex flex-col gap-1 border-0 p-0">
          <legend className="mb-1 text-sm font-semibold tracking-wide text-ink-soft uppercase">
            {group}
          </legend>
          {catalog
            .filter((n) => n.group === group)
            .map((n) => (
              <div key={n.type} className="rounded-xl border border-border bg-surface">
                <CheckRow
                  checked={value[n.type]}
                  onChange={(on) => onChange(n.type, on)}
                  title={n.label}
                  subtitle={value[n.type] ? 'Notify me' : 'Muted'}
                />
              </div>
            ))}
        </fieldset>
      ))}
    </div>
  );
}
